import { useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from '@/ui/Drawer';
import { Button } from '@/ui/Button';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useGuardarFicha, useMiFicha, type CampoFicha } from './api';

/**
 * MiFicha — los datos del maestro que el colaborador puede mantener él mismo.
 *
 * Era un modal; ahora es un panel lateral. La razón no es estética: el modal
 * tapaba el perfil, y este formulario existe justamente para completar lo que
 * al perfil le falta. Con el panel se ve detrás lo que se está arreglando.
 *
 * Lo que NO cambia, porque es donde está la seguridad del módulo:
 *
 *   · Los campos, sus tipos y sus opciones los decide el SERVIDOR. Cuando
 *     Bienestar agrega una columna a la Base de Datos Empleados, aparece aquí
 *     sola. Nada está escrito a mano en el cliente.
 *   · Lo que se pinta apagado no es una defensa: el guardado se vuelve a filtrar
 *     en el servidor. Aquí se apaga para no invitar, no para impedir.
 *   · Cada campo bloqueado dice POR QUÉ (`motivo`). Un campo gris sin
 *     explicación es un callejón sin salida.
 */
export function MiFicha({ abierta, onCerrar }: { abierta: boolean; onCerrar: () => void }) {
  const { data, isLoading, isError, error, refetch } = useMiFicha();
  const guardar = useGuardarFicha();

  const [valores, setValores] = useState<Record<string, string>>({});
  const [ids, setIds] = useState<Record<string, string>>({});
  const [intentado, setIntentado] = useState(false);

  const campos = useMemo(() => data?.campos ?? [], [data]);
  const editables = useMemo(() => campos.filter((c) => c.editable), [campos]);
  const ajenos = useMemo(() => campos.filter((c) => !c.editable), [campos]);

  // El borrador se siembra UNA VEZ POR APERTURA. Los dos matices de esa frase
  // son sendos defectos que el navegador encontró el 2026-09-04 y que el código
  // no delataba (la versión anterior dependía solo de `data`):
  //
  //   · «una vez»: `refetchOnWindowFocus` está activo, así que volver a la
  //     pestaña trae un objeto `data` nuevo aunque el contenido sea idéntico.
  //     Con `data` como única dependencia, eso resembraba el formulario y
  //     BORRABA lo que la persona estuviera escribiendo. Alt-tab y a empezar.
  //
  //   · «por apertura»: sin resembrar al abrir, «Cancelar» no cancelaba nada.
  //     Se vaciaba Teléfono, se cancelaba, se volvía a abrir y el campo seguía
  //     vacío — y el siguiente «Guardar cambios» habría guardado la edición que
  //     se creía descartada. No daba ningún error.
  const sembrado = useRef(false);
  useEffect(() => {
    if (!abierta) { sembrado.current = false; return; }
    if (sembrado.current || !data) return;
    const inicial: Record<string, string> = {};
    const inicialIds: Record<string, string> = {};
    for (const c of data.campos ?? []) {
      const v = data.ficha?.[c.key];
      inicial[c.key] = v == null ? '' : String(v);
      const idv = data.ficha?.[`${c.key}_id`];
      if (idv != null) inicialIds[c.key] = String(idv);
    }
    setValores(inicial);
    setIds(inicialIds);
    setIntentado(false);
    sembrado.current = true;
  }, [abierta, data]);

  const sucio = useMemo(() => {
    if (!data) return false;
    return editables.some((c) => {
      const original = data.ficha?.[c.key];
      return (valores[c.key] ?? '') !== (original == null ? '' : String(original));
    });
  }, [data, editables, valores]);

  useEffect(() => {
    window._appFormDirty = abierta && sucio;
    return () => { window._appFormDirty = false; };
  }, [abierta, sucio]);

  const faltante = editables.find((c) => c.required && !(valores[c.key] ?? '').trim());

  async function enviar() {
    setIntentado(true);
    if (faltante) {
      // El foco va al primer campo que falta. Sin esto, en un formulario de
      // quince campos el usuario tiene que buscar cuál es el rojo.
      document.getElementById(`ficha-${faltante.key}`)?.focus();
      return;
    }
    const carga: Record<string, string> = {};
    for (const c of editables) {
      carga[c.key] = (valores[c.key] ?? '').trim();
      if (c.type === 'employee' && ids[c.key]) carga[`${c.key}_id`] = ids[c.key]!;
    }
    try {
      const res = await guardar.mutateAsync(carga);
      window._appFormDirty = false;
      Utils.toast(res?.message || 'Datos actualizados', 'success');
      onCerrar();
    } catch (e) {
      Utils.toast(e instanceof Error ? e.message : 'No se pudo guardar', 'error');
    }
  }

  return (
    <Drawer
      abierto={abierta}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Mi ficha de colaborador"
      descripcion="Los datos que la empresa tiene de ti. Lo que puedas mantener al día, se edita aquí."
      ancho="md"
      pie={
        editables.length ? (
          <>
            <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
            <Button loading={guardar.isPending} onClick={() => void enviar()}>
              Guardar cambios
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
        )
      }
    >
      {isLoading && (
        <div className="flex flex-col gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
      )}

      {isError && (
        <EstadoError err={error} que="tu ficha" onReintentar={() => void refetch()} />
      )}

      {data && (
        <div className="flex flex-col gap-4">
          {editables.length === 0 && (
            <p className="text-3 text-muted">
              No hay datos que puedas actualizar por tu cuenta. Los administra Gestión Humana.
            </p>
          )}

          {editables.map((campo) => (
            <CampoDeFicha
              key={campo.key}
              campo={campo}
              valor={valores[campo.key] ?? ''}
              onCambio={(v) => setValores((prev) => ({ ...prev, [campo.key]: v }))}
              onElegirEmpleado={(nombre, id) => {
                setValores((prev) => ({ ...prev, [campo.key]: nombre }));
                setIds((prev) => ({ ...prev, [campo.key]: id }));
              }}
              error={
                intentado && campo.required && !(valores[campo.key] ?? '').trim()
                  ? 'Este dato es obligatorio.'
                  : undefined
              }
            />
          ))}

          {ajenos.length > 0 && (
            <details className="rounded-sm border border-line bg-sunken px-4 py-3">
              <summary className="cursor-pointer text-3 font-semibold text-ink">
                Ver mis datos laborales ({ajenos.length})
              </summary>
              <p className="mb-3 mt-2 text-2 text-muted">
                Los administra Gestión Humana. Si algo está mal, avísale a Bienestar.
              </p>
              <div className="flex flex-col gap-3">
                {ajenos.map((campo) => (
                  <Field
                    key={campo.key}
                    label={campo.label}
                    ayuda={campo.motivo || 'No editable'}
                  >
                    <Input
                      defaultValue={String(data.ficha?.[campo.key] ?? '')}
                      disabled
                      readOnly
                    />
                  </Field>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </Drawer>
  );
}

function CampoDeFicha({
  campo, valor, onCambio, onElegirEmpleado, error,
}: {
  campo: CampoFicha;
  valor: string;
  onCambio: (v: string) => void;
  onElegirEmpleado: (nombre: string, id: string) => void;
  error?: string;
}) {
  const texto = campo.sensible ? `${campo.label} · dato sensible` : campo.label;

  // La marca sustituye a la franja que este panel tenía arriba listando los
  // nombres de lo que faltaba (retirada el 2026-09-04). Aquella lista obligaba
  // a emparejar a ojo cinco nombres contra quince campos; la marca está en el
  // campo, que es donde se arregla, y se va sola en cuanto se escribe.
  //
  // «Sin dato» y no «falta»: el servidor cuenta como pendiente todo campo
  // editable vacío, sea obligatorio o no, así que un campo marcado «opcional»
  // también entra en la cuenta. «Falta» prometería una obligación que no hay.
  const sinDato = !valor.trim();
  const etiqueta = (
    <>
      {texto}
      {sinDato && (
        <span className="ml-2 rounded-full bg-warn/25 px-1.5 py-0.5 text-1 font-bold
                         uppercase tracking-wide text-warn-ink">
          sin dato
        </span>
      )}
    </>
  );

  if (campo.type === 'employee') {
    return (
      <CampoEmpleado
        campo={campo}
        etiqueta={etiqueta}
        valor={valor}
        onCambio={onCambio}
        onElegir={onElegirEmpleado}
        error={error}
      />
    );
  }

  return (
    <Field label={etiqueta} error={error} opcional={!campo.required} id={`ficha-${campo.key}`}>
      {campo.type === 'textarea' ? (
        <Textarea
          rows={3}
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
        />
      ) : campo.type === 'select' ? (
        <Select
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
        >
          <option value="">— Selecciona —</option>
          {(campo.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      ) : (
        <Input
          type={
            campo.type === 'number' ? 'number'
              : campo.type === 'date' ? 'date'
              : campo.type === 'email' ? 'email'
              : campo.type === 'phone' ? 'tel'
              : 'text'
          }
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
        />
      )}
    </Field>
  );
}

interface Sugerencia { id: number; label: string; sub?: string }

/**
 * Autocompletado de colaborador. Porta `Utils.campoEmpleadoWire` con la regla
 * que traía y que es la importante: **escribir a mano invalida la selección**.
 * El texto vuelve a ser «solo un nombre» hasta que se elija una sugerencia otra
 * vez, así que no se puede guardar un id que ya no corresponde al nombre.
 *
 * Se añade lo que la versión vanilla no tenía: navegación con flechas, Enter
 * para elegir y Escape para cerrar. Era una lista que solo aceptaba ratón.
 */
function CampoEmpleado({
  campo, etiqueta, valor, onCambio, onElegir, error,
}: {
  campo: CampoFicha;
  /** Ya trae la marca de «sin dato» si toca: la calcula `CampoDeFicha` para que
   *  los dos tipos de campo la lleven igual. */
  etiqueta: React.ReactNode;
  valor: string;
  onCambio: (v: string) => void;
  onElegir: (nombre: string, id: string) => void;
  error?: string;
}) {
  const [sugerencias, setSugerencias] = useState<Sugerencia[]>([]);
  const [abierta, setAbierta] = useState(false);
  const [activa, setActiva] = useState(-1);
  const caja = useRef<HTMLDivElement>(null);
  const temporizador = useRef<number>(0);

  useEffect(() => {
    function fuera(e: MouseEvent) {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierta(false);
    }
    document.addEventListener('click', fuera);
    return () => document.removeEventListener('click', fuera);
  }, []);

  function buscar(texto: string) {
    window.clearTimeout(temporizador.current);
    if (texto.trim().length < 2) { setSugerencias([]); setAbierta(false); return; }
    temporizador.current = window.setTimeout(async () => {
      try {
        const res = await api.get<Sugerencia[]>('/auth/colaboradores', {
          campo: campo.key, q: texto.trim(),
        });
        setSugerencias(res ?? []);
        setActiva(-1);
        setAbierta(true);
      } catch {
        setAbierta(false);
      }
    }, 250);
  }

  return (
    <div ref={caja} className="relative">
      <Field label={etiqueta} error={error} opcional={!campo.required}
             id={`ficha-${campo.key}`}
             ayuda="Escribe al menos dos letras y elige de la lista.">
        <Input
          autoComplete="off"
          value={valor}
          placeholder="Escribe un nombre…"
          onChange={(e) => {
            // Invalida la selección previa: el id deja de valer en cuanto el
            // texto deja de ser el que se eligió.
            onElegir(e.target.value, '');
            onCambio(e.target.value);
            buscar(e.target.value);
          }}
          onKeyDown={(e) => {
            if (!abierta || !sugerencias.length) return;
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiva((i) => (i + 1) % sugerencias.length); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActiva((i) => (i - 1 + sugerencias.length) % sugerencias.length); }
            if (e.key === 'Escape') { setAbierta(false); }
            if (e.key === 'Enter' && activa >= 0) {
              e.preventDefault();
              const s = sugerencias[activa];
              if (s) { onElegir(s.label, String(s.id)); setAbierta(false); }
            }
          }}
        />
      </Field>

      {abierta && (
        <ul
          className="absolute z-dropdown mt-1 max-h-64 w-full overflow-y-auto rounded-sm
                     border border-line bg-surface shadow-raised"
          role="listbox"
        >
          {sugerencias.length === 0 ? (
            <li className="px-3 py-2 text-3 text-muted">Sin resultados</li>
          ) : (
            sugerencias.map((s, i) => (
              <li key={s.id} role="option" aria-selected={i === activa}>
                <button
                  type="button"
                  onClick={() => { onElegir(s.label, String(s.id)); setAbierta(false); }}
                  className={cn(
                    'block w-full px-3 py-2 text-left text-3 text-ink',
                    i === activa ? 'bg-sunken' : 'hover:bg-sunken',
                  )}
                >
                  {s.label}
                  {s.sub && <span className="ml-1 text-muted">· {s.sub}</span>}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
